import { useState, useCallback, useEffect } from 'react'
import { useAuthStore } from '../store/index.js'

export interface TenantUser {
  id: string
  email: string
  fullName: string | null
  status: string
  createdAt: string
  roles: string[]
}

export interface UseTenantUsersResult {
  users: TenantUser[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  updatePassword: (userId: string, newPassword: string) => Promise<void>
  createUser: (email: string, fullName: string, temporaryPassword: string) => Promise<void>
}

export function useTenantUsers(tenantId: string): UseTenantUsersResult {
  const [users, setUsers] = useState<TenantUser[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const csrfToken = useAuthStore((s: { csrfToken: string | null }) => s.csrfToken)

  const refetch = useCallback(async (): Promise<void> => {
    if (!tenantId) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`/api/tenants/${tenantId}/users`, {
        method: 'GET',
        credentials: 'include',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message || 'Failed to fetch users')
      }
      const data = await res.json()
      setUsers(data.users || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }, [tenantId])

  const updatePassword = useCallback(
    async (userId: string, newPassword: string): Promise<void> => {
      if (!tenantId) return
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (csrfToken) headers['x-csrf-token'] = csrfToken

      const res = await fetch(`/api/tenants/${tenantId}/users/${userId}/password`, {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: JSON.stringify({ newPassword }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message || 'Failed to update password')
      }
    },
    [tenantId, csrfToken],
  )

  const createUser = useCallback(
    async (email: string, fullName: string, temporaryPassword: string): Promise<void> => {
      if (!tenantId) return
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (csrfToken) headers['x-csrf-token'] = csrfToken

      const res = await fetch(`/api/tenants/${tenantId}/admin-user`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ email, fullName, temporaryPassword }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.message || 'Failed to create user')
      }
      await refetch()
    },
    [tenantId, csrfToken, refetch],
  )

  // Initial fetch
  useEffect(() => {
    void refetch()
  }, [refetch])

  return { users, loading, error, refetch, updatePassword, createUser }
}
